import { FontIconName, TreeAccordion } from "client/Components";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { AddressBookContactSource } from "./Overview/Overview";
import { generateTrackingId, isNil } from "client/Misc/utils";
import {
	ExchangeAvailability,
	useStableImperativeCallback,
	useSynchronizedRef
} from "client/Hooks";
import {
	ContactSource,
	listWorkspaceContactsDepartments,
	listWorkspaceContactsOfficeLocations
} from "client/Api/Email/contacts";
import { partition } from "client_legacy/ndaw/arrayHelpers";
import { never } from "client_legacy/ndaw/errors";
import { ConfigurableAddressBookAppContactSource } from "client/Api/Apps/EditApp/saveApp";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";

export type SourceSelectorProps = {
	options: readonly SourceOption[];
	value: SourceSelectorValue | undefined;
	onChange: (value: SourceSelectorValue) => void;
};

export const SourceSelector = (props: SourceSelectorProps): ReactElement => {
	const {
		options,
		value,
		onChange
	} = props;

	const [workspaceOption, otherOptions] = useMemo(
		(): [workspaceSource: SourceOption | undefined, otherSources: readonly SourceOption[]] => {
			const [workspaceSources, otherSources] = partition(
				options,
				(x) => x.source === ContactSource.Workspace
			);

			return [workspaceSources[0], otherSources];
		},
		[options]
	);

	return (
		<TreeAccordion trackingId="contact-source-selector">
			{workspaceOption && (
				<WorkspaceSource
					option={workspaceOption}
					value={value}
					onChange={onChange}
				/>
			)}

			{otherOptions.map((x): ReactElement => (
				<TreeAccordion.Node key={x.source}>
					<TreeAccordion.Item
						icon={x.icon}
						isSelected={value?.source === x.source}
						onClick={() => onChange({ source: x.source })}
						expansionToggle="hidden"
						trackingId={generateTrackingId("contact-source", `|${x.valueTrackingId}`)}
						title={localize(x.textKey)}
					>
						{localize(x.textKey)}
					</TreeAccordion.Item>
				</TreeAccordion.Node>
			))}
		</TreeAccordion>
	);
};
SourceSelector.displayName = "SourceSelector";

const WORKSPACE_ROOT_ID = "workspace_root";
const DEPARTMENT_TRACKING_ID = "department";
const LOCATION_TRACKING_ID = "location";

type WorkspaceSourceProps = {
	option: SourceOption;
	value: SourceSelectorValue | undefined;
	onChange: (value: SourceSelectorValue) => void;
};

const WorkspaceSource = (props: WorkspaceSourceProps): ReactElement => {
	const {
		option,
		value,
		onChange
	} = props;

	if (option.source !== ContactSource.Workspace) {
		throw new Error("Only the Workspace source is supported");
	}

	const [expandedIds, setExpandedIds] = useState<Set<string>>(
		() => {
			const result = new Set<string>();
			result.add(WORKSPACE_ROOT_ID);

			if (value?.source === ContactSource.Workspace) {

				if (value.filter?.kind === "department") {
					result.add(DEPARTMENT_TRACKING_ID);
				}

				if (value.filter?.kind === "officeLocation") {
					result.add(LOCATION_TRACKING_ID);
				}
			}

			return result;
		}
	);

	const [departments, setDepartments] = useState<readonly SourceFilter[] | null>(null);
	const [locations, setLocations] = useState<readonly SourceFilter[] | null>(null);

	const valueRef: ReadonlyRefObject<SourceSelectorValue | undefined> = useSynchronizedRef(value);
	const stableOnChange: typeof onChange = useStableImperativeCallback(onChange);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				const [departmentResult, locationResult] = await Promise.allSettled([
					listWorkspaceContactsDepartments(signal),
					listWorkspaceContactsOfficeLocations(signal)
				]);

				const departments: readonly SourceFilter[] = departmentResult.status === "fulfilled"
					? departmentResult.value
						.map((x): SourceFilter => ({
							value: x,
							kind: "department"
						}))
					: [];

				const locations: readonly SourceFilter[] = locationResult.status === "fulfilled"
					? locationResult.value
						.map((x): SourceFilter => ({
							value: x,
							kind: "officeLocation"
						}))
					: [];

				setDepartments(departments);
				setLocations(locations);

				const { current: value } = valueRef;
				if (value?.source === ContactSource.Workspace && value.filter) {
					let items: readonly SourceFilter[];

					switch (value.filter.kind) {
						case "department":
							items = departments;
							break;
						case "officeLocation":
							items = locations;
							break;
						default:
							never(
								value.filter
									.kind
							);
					}

					const filterValue: string = value.filter
						.value
						.toUpperCase();

					const filterExists: boolean = items.some(x => x.value.toUpperCase() === filterValue);

					if (!filterExists) {
						stableOnChange({
							source: ContactSource.Workspace
						});
					}
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[stableOnChange, valueRef]
	);

	const filterSections = useMemo(
		(): readonly FilterSectionConfiguration[] => {
			const result: FilterSectionConfiguration[] = [];
			if (departments === null || departments.length > 0) {
				result.push({
					trackingId: DEPARTMENT_TRACKING_ID,
					text: localize("_Department"),
					items: departments
				});
			}

			if (locations === null || locations.length > 0) {
				result.push({
					trackingId: LOCATION_TRACKING_ID,
					text: localize("_Location"),
					items: locations
				});
			}

			return result;
		},
		[departments, locations]
	);

	const toggleCollapsed = useCallback(
		(id: string): void => {
			setExpandedIds(
				(x) => {
					const result = new Set(x);
					if (result.has(id)) {
						result.delete(id);
					} else {
						result.add(id);
					}

					return result;
				}
			);
		},
		[]
	);

	const filterClickedHandler = useCallback(
		(filter: SourceFilter): void => {
			onChange({ source: ContactSource.Workspace, filter });
		},
		[onChange]
	);

	return (
		<TreeAccordion.Node>
			<TreeAccordion.Item
				icon={option.icon}
				expansionToggle="visible"
				isExpanded={expandedIds.has(WORKSPACE_ROOT_ID)}
				trackingId={generateTrackingId("contact-source", `|${option.valueTrackingId}`)}
				title={localize(option.textKey)}
				onClick={() => toggleCollapsed(WORKSPACE_ROOT_ID)}
				onToggleExpansion={() => toggleCollapsed(WORKSPACE_ROOT_ID)}
			>
				{localize(option.textKey)}
			</TreeAccordion.Item>

			<TreeAccordion.SubTree isCollapsed={!expandedIds.has(WORKSPACE_ROOT_ID)}>
				<TreeAccordion.Node>
					<TreeAccordion.Item
						indentionLevel={1}
						isSelected={value?.source === ContactSource.Workspace && isNil(value.filter)}
						onClick={() => onChange({ source: ContactSource.Workspace })}
						expansionToggle={filterSections.length > 0 ? "hidden" : "none"}
						trackingId="all-contacts"
						title={localize("_AllContacts")}
					>
						{localize("_AllContacts")}
					</TreeAccordion.Item>
				</TreeAccordion.Node>

				{filterSections.map((s): ReactElement => (
					<TreeAccordion.Node key={s.trackingId}>
						<TreeAccordion.Item
							{...(s.items !== null
								? {
									expansionToggle: "visible",
									isExpanded: expandedIds.has(s.trackingId),
									onToggleExpansion: () => toggleCollapsed(s.trackingId)
								}
								: {
									expansionToggle: "hidden"
								}
							)}
							indentionLevel={1}
							onClick={() => toggleCollapsed(s.trackingId)}
							trackingId={s.trackingId}
							title={s.text}
						>
							{s.items === null
								? <TreeAccordion.Skeleton />
								: s.text
							}
						</TreeAccordion.Item>

						{s.items !== null && (
							<TreeAccordion.SubTree
								isCollapsed={!expandedIds.has(s.trackingId)}
								trackingId={generateTrackingId(s.trackingId, "__items")}
							>
								{s.items.map((x): ReactElement => (
									<TreeAccordion.Node key={x.value}>
										<TreeAccordion.Item
											indentionLevel={2}
											isSelected={
												value?.source === ContactSource.Workspace
												&& isActiveFilter(value.filter, x)
											}
											onClick={() => filterClickedHandler(x)}
											trackingId={x.value}
											title={x.value}
										>
											{x.value}
										</TreeAccordion.Item>
									</TreeAccordion.Node>
								))}
							</TreeAccordion.SubTree>
						)}
					</TreeAccordion.Node>
				))}
			</TreeAccordion.SubTree>
		</TreeAccordion.Node>
	);
};
WorkspaceSource.displayName = "WorkspaceSource";

function isActiveFilter(activeFilter: SourceFilter | undefined, filter: SourceFilter): boolean {
	if (!activeFilter) {
		return false;
	}

	return activeFilter.kind === filter.kind
		&& activeFilter.value.toUpperCase() === filter.value.toUpperCase();
}

export type SourceFilter = {
	kind: "department" | "officeLocation";
	value: string;
};

export type SourceSelectorValue = {
	source: Exclude<AddressBookContactSource, ContactSource.Workspace>;
	filter?: undefined;
} | {
	source: ContactSource.Workspace;
	filter?: SourceFilter;
};

export type SourceOption = {
	source: AddressBookContactSource;
	valueTrackingId: string;
	icon: FontIconName;
	textKey: LocalizationKey;
};

export function useAvailableSourceOptions(
	exchangeAvailability: ExchangeAvailability
): readonly SourceOption[] {
	return useMemo(
		(): readonly SourceOption[] => {
			const result: SourceOption[] = [];

			result.push(contactSourceToSourceOptionMap[ContactSource.Workspace]);

			const {
				configurableContactSources,
				isAppAvailable
			} = userConfiguration().addressBook;

			if (isAppAvailable && exchangeAvailability === ExchangeAvailability.Available) {
				if (configurableContactSources.includes(ConfigurableAddressBookAppContactSource.Personal)){
					result.push(contactSourceToSourceOptionMap[ContactSource.Personal]);
				}

				if (configurableContactSources.includes(ConfigurableAddressBookAppContactSource.Gal)){
					result.push(contactSourceToSourceOptionMap[ContactSource.Gal]);
				}
			}

			return result;
		},
		[exchangeAvailability]
	);
}

export const contactSourceToSourceOptionMap: Record<Exclude<ContactSource, ContactSource.Suggested>, SourceOption> = {
	[ContactSource.Personal]: {
		source: ContactSource.Personal,
		icon: "user",
		textKey: "_Personal(Contacts)",
		valueTrackingId: "personal"
	},
	[ContactSource.Gal]: {
		source: ContactSource.Gal,
		icon: "buildings",
		textKey: "_Organisation(Contacts)",
		valueTrackingId: "organisation"
	},
	[ContactSource.Workspace]: {
		source: ContactSource.Workspace,
		icon: "workspace",
		textKey: "_Workspace(Contacts)",
		valueTrackingId: "workspace"
	}
};

type FilterSectionConfiguration = {
	text: string;
	trackingId: string;
	items: readonly SourceFilter[] | null;
};
